import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { format, parse, isValid } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Calculator as CalcIcon, FileDown, Scale, DollarSign, Clock, TrendingUp, Settings, Plus, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { generatePDF } from "@/lib/pdfGenerator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Calculator = () => {
  const [judgmentDate, setJudgmentDate] = useState(null);
  const [calculationDate, setCalculationDate] = useState(null);
  const [judgmentDateInput, setJudgmentDateInput] = useState("");
  const [calculationDateInput, setCalculationDateInput] = useState("");
  const [judgmentAmount, setJudgmentAmount] = useState("");
  const [courtCosts, setCourtCosts] = useState("");
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [judgmentDateOpen, setJudgmentDateOpen] = useState(false);
  const [calculationDateOpen, setCalculationDateOpen] = useState(false);
  
  // Rate management state
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [rates, setRates] = useState({});
  const [editingYear, setEditingYear] = useState(null);
  const [newYear, setNewYear] = useState("");
  const [newRate, setNewRate] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Parse date from text input (supports MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY)
  const parseUserDate = (input) => {
    if (!input) return null;
    
    // Try various formats
    const formats = ['MM/dd/yyyy', 'M/d/yyyy', 'MM-dd-yyyy', 'M-d-yyyy', 'yyyy-MM-dd'];
    for (const fmt of formats) {
      const parsed = parse(input, fmt, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    }
    return null;
  };

  // Handle judgment date text input
  const handleJudgmentDateInput = (value) => {
    setJudgmentDateInput(value);
    const parsed = parseUserDate(value);
    if (parsed) {
      setJudgmentDate(parsed);
    }
  };

  // Handle calculation date text input
  const handleCalculationDateInput = (value) => {
    setCalculationDateInput(value);
    const parsed = parseUserDate(value);
    if (parsed) {
      setCalculationDate(parsed);
    }
  };

  // Sync text input when calendar selection changes
  const handleJudgmentDateSelect = (date) => {
    setJudgmentDate(date);
    if (date) {
      setJudgmentDateInput(format(date, 'MM/dd/yyyy'));
    }
    setJudgmentDateOpen(false);
  };

  const handleCalculationDateSelect = (date) => {
    setCalculationDate(date);
    if (date) {
      setCalculationDateInput(format(date, 'MM/dd/yyyy'));
    }
    setCalculationDateOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate) => {
    return `${rate.toFixed(2)}%`;
  };

  // Fetch rates when dialog opens
  const fetchRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rates`);
      setRates(response.data.rates);
    } catch (error) {
      console.error("Failed to fetch rates:", error);
      toast.error("Failed to load interest rates");
    }
  }, []);

  useEffect(() => {
    if (ratesDialogOpen) {
      fetchRates();
    }
  }, [ratesDialogOpen, fetchRates]);

  const handleSaveRate = async (year, rate) => {
    if (!adminPassword) {
      toast.error("Please enter the admin password");
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(`${API}/rates`, {
        year: parseInt(year),
        rate: parseFloat(rate),
        password: adminPassword
      });
      toast.success(`Rate for ${year} saved successfully`);
      await fetchRates();
      setEditingYear(null);
      setIsAddingNew(false);
      setNewYear("");
      setNewRate("");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid password");
      } else {
        toast.error(error.response?.data?.detail || "Failed to save rate");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRate = async (year) => {
    if (!adminPassword) {
      toast.error("Please enter the admin password");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the rate for ${year}?`)) {
      return;
    }

    setIsSaving(true);
    try {
      await axios.delete(`${API}/rates`, {
        data: {
          year: parseInt(year),
          password: adminPassword
        }
      });
      toast.success(`Rate for ${year} deleted`);
      await fetchRates();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid password");
      } else {
        toast.error(error.response?.data?.detail || "Failed to delete rate");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCalculate = useCallback(async () => {
    if (!judgmentDate || !calculationDate || !judgmentAmount) {
      toast.error("Please fill in all fields");
      return;
    }

    const amount = parseFloat(judgmentAmount.replace(/[,$]/g, ''));
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid judgment amount");
      return;
    }

    const costs = courtCosts ? parseFloat(courtCosts.replace(/[,$]/g, '')) : 0;
    if (isNaN(costs) || costs < 0) {
      toast.error("Please enter a valid court costs amount");
      return;
    }

    if (calculationDate <= judgmentDate) {
      toast.error("Calculation date must be after judgment date");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, {
        judgment_date: format(judgmentDate, 'yyyy-MM-dd'),
        judgment_amount: amount,
        calculation_date: format(calculationDate, 'yyyy-MM-dd'),
        court_costs: costs
      });
      setResult(response.data);
      toast.success("Calculation completed");
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate interest");
    } finally {
      setIsLoading(false);
    }
  }, [judgmentDate, calculationDate, judgmentAmount, courtCosts]);

  const handleExportPDF = useCallback(() => {
    if (!result) return;
    generatePDF(result);
    toast.success("PDF exported successfully");
  }, [result]);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setJudgmentAmount(value);
  };

  const handleCourtCostsChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCourtCosts(value);
  };

  const handleCourtCostsBlur = () => {
    if (courtCosts) {
      const amount = parseFloat(courtCosts);
      if (!isNaN(amount)) {
        setCourtCosts(amount.toFixed(2));
      }
    }
  };

  const handleAmountBlur = () => {
    if (judgmentAmount) {
      const amount = parseFloat(judgmentAmount);
      if (!isNaN(amount)) {
        setJudgmentAmount(amount.toFixed(2));
      }
    }
  };

  const sortedYears = Object.keys(rates).map(Number).sort((a, b) => b - a);

  return (
    <div className="calculator-container" data-testid="calculator-container">
      {/* Header */}
      <header className="bg-[#0f172a] text-white py-6 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Scale className="h-8 w-8 text-blue-400" />
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">
                JurisCalc NJ
              </h1>
              <p className="text-slate-400 text-sm mt-0.5">
                New Jersey Post-Judgment Interest Calculator
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRatesDialogOpen(true)}
            data-testid="manage-rates-button"
            className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Rates
          </Button>
        </div>
      </header>

      {/* Rate Management Dialog */}
      <Dialog open={ratesDialogOpen} onOpenChange={setRatesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="rates-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Manage Interest Rates</DialogTitle>
            <DialogDescription>
              Add or edit yearly interest rates as published by the court.
            </DialogDescription>
          </DialogHeader>
          
          {/* Password Input */}
          <div className="space-y-2 py-4 border-b">
            <Label htmlFor="admin-password" className="text-sm font-medium">
              Admin Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Enter password to make changes"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              data-testid="admin-password-input"
              className="max-w-xs"
            />
          </div>

          {/* Rates Table */}
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Year</TableHead>
                  <TableHead className="font-semibold">Rate (%)</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Add New Row */}
                {isAddingNew && (
                  <TableRow data-testid="add-rate-row">
                    <TableCell>
                      <Input
                        type="number"
                        placeholder="Year"
                        value={newYear}
                        onChange={(e) => setNewYear(e.target.value)}
                        className="w-24"
                        data-testid="new-year-input"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Rate"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                        className="w-24"
                        data-testid="new-rate-input"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveRate(newYear, newRate)}
                          disabled={isSaving || !newYear || !newRate}
                          data-testid="save-new-rate-button"
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsAddingNew(false);
                            setNewYear("");
                            setNewRate("");
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Existing Rates */}
                {sortedYears.map((year) => (
                  <TableRow key={year} data-testid={`rate-row-${year}`}>
                    <TableCell className="font-medium">{year}</TableCell>
                    <TableCell>
                      {editingYear === year ? (
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={rates[year]}
                          onChange={(e) => setNewRate(e.target.value)}
                          className="w-24"
                          data-testid={`edit-rate-input-${year}`}
                        />
                      ) : (
                        <span className="font-mono">{rates[year].toFixed(2)}%</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingYear === year ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveRate(year, newRate || rates[year])}
                            disabled={isSaving}
                            data-testid={`save-rate-button-${year}`}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingYear(null);
                              setNewRate("");
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingYear(year);
                              setNewRate(rates[year].toString());
                            }}
                            data-testid={`edit-rate-button-${year}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteRate(year)}
                            data-testid={`delete-rate-button-${year}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddingNew(true);
                setEditingYear(null);
              }}
              disabled={isAddingNew}
              data-testid="add-new-rate-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Input Panel */}
          <div className="lg:w-96 lg:sticky lg:top-8 lg:self-start">
            <Card className="input-card" data-testid="input-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <CalcIcon className="h-5 w-5 text-blue-700" />
                  Calculate Interest
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Judgment Date */}
                <div className="space-y-2">
                  <Label htmlFor="judgment-date" className="text-sm font-medium text-slate-700">
                    Judgment Date
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="judgment-date"
                      type="text"
                      placeholder="MM/DD/YYYY"
                      data-testid="judgment-date-input"
                      value={judgmentDateInput}
                      onChange={(e) => handleJudgmentDateInput(e.target.value)}
                      className="h-12 font-mono text-base flex-1"
                    />
                    <Popover open={judgmentDateOpen} onOpenChange={setJudgmentDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="judgment-date-picker"
                          className="h-12 w-12 p-0"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={judgmentDate}
                          onSelect={handleJudgmentDateSelect}
                          initialFocus
                          disabled={(date) => date > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Judgment Amount */}
                <div className="space-y-2">
                  <Label htmlFor="judgment-amount" className="text-sm font-medium text-slate-700">
                    Judgment Amount
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="judgment-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      data-testid="judgment-amount-input"
                      value={judgmentAmount}
                      onChange={handleAmountChange}
                      onBlur={handleAmountBlur}
                      className="h-12 pl-9 font-mono text-base"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    +2% rate applies for amounts over $20,000
                  </p>
                </div>

                {/* Calculation Date */}
                <div className="space-y-2">
                  <Label htmlFor="calculation-date" className="text-sm font-medium text-slate-700">
                    Calculate Through Date
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="calculation-date"
                      type="text"
                      placeholder="MM/DD/YYYY"
                      data-testid="calculation-date-input"
                      value={calculationDateInput}
                      onChange={(e) => handleCalculationDateInput(e.target.value)}
                      className="h-12 font-mono text-base flex-1"
                    />
                    <Popover open={calculationDateOpen} onOpenChange={setCalculationDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="calculation-date-picker"
                          className="h-12 w-12 p-0"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={calculationDate}
                          onSelect={handleCalculationDateSelect}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Court Costs */}
                <div className="space-y-2">
                  <Label htmlFor="court-costs" className="text-sm font-medium text-slate-700">
                    Court Costs <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="court-costs"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      data-testid="court-costs-input"
                      value={courtCosts}
                      onChange={handleCourtCostsChange}
                      onBlur={handleCourtCostsBlur}
                      className="h-12 pl-9 font-mono text-base"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Flat fee added to total (not subject to interest)
                  </p>
                </div>

                {/* Calculate Button */}
                <Button
                  onClick={handleCalculate}
                  disabled={isLoading}
                  data-testid="calculate-button"
                  className="w-full h-12 bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium"
                >
                  {isLoading ? (
                    <span className="loading-pulse">Calculating...</span>
                  ) : (
                    <>
                      <CalcIcon className="mr-2 h-4 w-4" />
                      Calculate Interest
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="flex-1 min-w-0">
            {result ? (
              <div className="space-y-6 fade-in" data-testid="results-panel">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Amount Due */}
                  <Card className="summary-card col-span-1 md:col-span-2" data-testid="total-amount-card">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-slate-400 text-sm uppercase tracking-wider mb-1">
                            Total Amount Due
                          </p>
                          <p className="currency-large text-white" data-testid="total-amount-value">
                            {formatCurrency(result.total_amount_due)}
                          </p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-blue-400" />
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-slate-500 text-xs uppercase tracking-wider">Principal</p>
                          <p className="font-mono text-slate-300 mt-1">
                            {formatCurrency(result.judgment_amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs uppercase tracking-wider">Total Interest</p>
                          <p className="font-mono text-emerald-400 mt-1" data-testid="total-interest-value">
                            {formatCurrency(result.total_interest)}
                          </p>
                        </div>
                        {result.court_costs > 0 && (
                          <div>
                            <p className="text-slate-500 text-xs uppercase tracking-wider">Court Costs</p>
                            <p className="font-mono text-slate-300 mt-1" data-testid="court-costs-value">
                              {formatCurrency(result.court_costs)}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Days & Rate Info */}
                  <Card className="results-card" data-testid="days-info-card">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="h-5 w-5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-600">Period Details</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Days</p>
                          <p className="font-mono text-xl font-semibold text-slate-800" data-testid="total-days-value">
                            {result.total_days.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Judgment Amount</p>
                          <span className={cn(
                            "threshold-badge mt-1",
                            result.is_over_threshold ? "over" : "under"
                          )} data-testid="threshold-badge">
                            {result.is_over_threshold ? "Over $20,000 (+2%)" : "Under $20,000"}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Export Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleExportPDF}
                    variant="outline"
                    data-testid="export-pdf-button"
                    className="gap-2"
                  >
                    <FileDown className="h-4 w-4" />
                    Export to PDF
                  </Button>
                </div>

                {/* Year-by-Year Breakdown */}
                <Card className="results-card" data-testid="breakdown-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">
                      Year-by-Year Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Year</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Period</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">Days</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">Base Rate</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">+2% Add.</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">Eff. Rate</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">Interest</TableHead>
                            <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider text-right">Running Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {result.yearly_breakdown.map((row, index) => (
                            <TableRow key={index} data-testid={`breakdown-row-${row.year}`}>
                              <TableCell className="font-medium">{row.year}</TableCell>
                              <TableCell className="font-mono text-sm text-slate-600">
                                {format(new Date(row.start_date), 'MM/dd')} - {format(new Date(row.end_date), 'MM/dd')}
                              </TableCell>
                              <TableCell className="text-right font-mono">{row.days}</TableCell>
                              <TableCell className="text-right font-mono">{formatPercentage(row.base_rate)}</TableCell>
                              <TableCell className="text-right font-mono">
                                {row.additional_rate > 0 ? formatPercentage(row.additional_rate) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium text-blue-700">
                                {formatPercentage(row.effective_rate)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-emerald-600">
                                {formatCurrency(row.interest)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium">
                                {formatCurrency(row.running_total)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Calculation Details */}
                <Card className="results-card" data-testid="calculation-details-card">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-medium mb-4">Calculation Details</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Judgment Date</p>
                        <p className="font-mono text-slate-800 mt-1">
                          {format(new Date(result.judgment_date), 'MM/dd/yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Calculation Date</p>
                        <p className="font-mono text-slate-800 mt-1">
                          {format(new Date(result.calculation_date), 'MM/dd/yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Original Judgment</p>
                        <p className="font-mono text-slate-800 mt-1">
                          {formatCurrency(result.judgment_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Interest Accrued</p>
                        <p className="font-mono text-emerald-600 mt-1">
                          {formatCurrency(result.total_interest)}
                        </p>
                      </div>
                      {result.court_costs > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Court Costs</p>
                          <p className="font-mono text-slate-800 mt-1">
                            {formatCurrency(result.court_costs)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 text-center" data-testid="empty-state">
                <div className="max-w-md">
                  <Scale className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h2 className="font-heading text-2xl text-slate-700 mb-2">
                    Calculate Post-Judgment Interest
                  </h2>
                  <p className="text-slate-500">
                    Enter the judgment date, amount, and calculation date to compute 
                    New Jersey post-judgment interest with a detailed year-by-year breakdown.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="legal-disclaimer max-w-4xl">
          <p>
            <strong>Disclaimer:</strong> This calculator is provided for informational purposes only 
            and should not be considered legal advice. Interest rates are based on the New Jersey 
            Court Rules. For judgments over $20,000, an additional 2% is added to the base rate 
            per N.J.S.A. 2A:17-56.51. Please verify all calculations with official court records 
            and consult with a licensed attorney for legal matters.
          </p>
        </div>
      </main>
    </div>
  );
};
